// T22.641 keep-wheels math. PURE: no React, no network.
//
// The SAME rule the server re-validates (Backend
// subscription/downgrade_archive/selection_math.py::evaluate_keep_set):
//   storage: kept wheel bytes + Trash bytes <= new storage limit
//   wheels:  kept wheel count (created + cloned together) <= new wheel limit
// Save is allowed only when BOTH fit. `null`/`undefined` limit = unlimited.

export const SORT_COLUMNS = [
  { key: "name", label: "Wheel" },
  { key: "size", label: "Size" },
  { key: "updated_at", label: "Last edited" },
  { key: "views", label: "Views" },
  { key: "clones", label: "Clones" },
  { key: "favorites", label: "Favorites" },
  { key: "unlocks", label: "Unlocks" },
];

export const DEFAULT_SORT = Object.freeze({ key: "updated_at", dir: "desc" });

const sortValue = (row, key) => {
  if (key === "name") return String(row?.name || "").toLocaleLowerCase();
  if (key === "updated_at") {
    const t = Date.parse(row?.updated_at || "");
    return Number.isFinite(t) ? t : 0;
  }
  const n = Number(row?.[key]);
  return Number.isFinite(n) ? n : 0;
};

/** Stable sort of the popup rows by one column. Ties keep their order. */
export const sortWheels = (rows, key = DEFAULT_SORT.key, dir = DEFAULT_SORT.dir) => {
  const sign = dir === "asc" ? 1 : -1;
  return (rows || [])
    .map((row, i) => ({ row, i }))
    .sort((a, b) => {
      const va = sortValue(a.row, key);
      const vb = sortValue(b.row, key);
      if (va < vb) return -1 * sign;
      if (va > vb) return 1 * sign;
      return a.i - b.i;
    })
    .map(({ row }) => row);
};

/** Clicking a header: same column flips direction, a new column starts at
 *  descending for numbers/dates (biggest first) and ascending for the name. */
export const nextSort = (current, key) => {
  if (current?.key === key) {
    return { key, dir: current.dir === "asc" ? "desc" : "asc" };
  }
  return { key, dir: key === "name" ? "asc" : "desc" };
};

const toInt = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
};

export const evaluateKeepSet = (
  rows,
  keepIds,
  wheelLimit,
  storageLimit,
  trashBytes = 0
) => {
  const byId = new Map((rows || []).map((r) => [String(r.id), r]));
  const kept = [];
  (keepIds || []).forEach((id) => {
    const key = String(id);
    if (byId.has(key) && !kept.includes(key)) kept.push(key);
  });
  const keptBytes = kept.reduce((sum, id) => sum + toInt(byId.get(id)?.size), 0);
  const usedBytes = keptBytes + toInt(trashBytes);
  const hasWheelLimit = wheelLimit !== null && wheelLimit !== undefined;
  const hasStorageLimit = storageLimit !== null && storageLimit !== undefined;
  const fitsWheels = !hasWheelLimit || kept.length <= Number(wheelLimit);
  const fitsStorage = !hasStorageLimit || usedBytes <= Number(storageLimit);
  return {
    keptCount: kept.length,
    keptBytes,
    usedBytes,
    fitsWheels,
    fitsStorage,
    fits: fitsWheels && fitsStorage,
  };
};

/** Human bytes, base-10 like the Storage page ("1.2 GB", "250 MB"). */
export const formatBytes = (bytes) => {
  const n = toInt(bytes);
  if (n >= 1e9) return `${(n / 1e9).toFixed(n % 1e9 === 0 ? 0 : 1)} GB`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(n % 1e6 === 0 ? 0 : 1)} MB`;
  if (n >= 1e3) return `${Math.round(n / 1e3)} KB`;
  return `${n} B`;
};
